import React from 'react';
import BlogArticle from 'components/blog/blogArticle';
import { Post } from 'components/blog/content/styled';
import { authorDorian as author } from 'data/authors';
import img from 'img/blog/cognitive-psychology.png';

import img1 from 'img/blog/schema-theory.png';
import img2 from 'img/blog/cluttered-website.png';
import img3 from 'img/blog/schema-theory-ex.png';
import img4 from 'img/blog/babbels-app.jpg';
import img5 from 'img/blog/icora-web.png';
import img6 from 'img/blog/cnn.png';
import img7 from 'img/blog/proximity.png';
import img8 from 'img/blog/chameleon-eff.png';

const Content = () => {
  return (
    <Post>
      <p>
        If you’ve been working in the field for a while, you’re probably familiar with these basic
        UX design principles:
      </p>
      <ul>
        <li>Keep the design consistent.</li>
        <li>Focus on usability.</li>
        <li>Meet the user’s needs.</li>
      </ul>
      <p>
        Back in 2009, Whitney Hess, a popular UX design coach, drafted the{' '}
        <a
          href="https://whitneyhess.com/blog/2009/10/02/a-hippocratic-oath-for-user-experience-designers/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Hippocratic Oath for UX designers
        </a>
        . In this piece, she listed a few principles that a UX designer should keep up with. These
        include:
      </p>
      <ul>
        <li>
          Applying all the measures required to create a good design for the benefit of the user.
        </li>
        <li>
          Remembering that the art of UX design does not exist without warmth, sympathy, and
          understanding.
        </li>
        <li>Respecting the privacy of the users.</li>
      </ul>
      <p>
        All these points imply that the designer should focus on the needs of the user. Good design
        comes from the focus on effortless navigation, good readability, high accessibility, and
        overall usability.
      </p>
      <p>
        To make it work, your UX designer portfolio should not only focus on the quality of your
        professional skills. There is much more to a good UX design than just having technical
        skills.
      </p>
      <p>
        To a certain extent, a good UX designer should also be a{' '}
        <a
          href="/blog/7-psychological-principles-for-better-ux/"
          target="_blank"
          rel="noopener noreferrer"
        >
          good psychologist
        </a>
        , understand human nature and what drives the consumer to choose one product over the other.{' '}
        <strong>Cognitive psychology</strong> can be particularly helpful in creating high-quality
        UX design.
      </p>
      <h2 id="what-is-cognitive-psychology">What is Cognitive Psychology</h2>
      <p>
        The definition of{' '}
        <a
          href="https://www.simplypsychology.org/cognitive.html"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          cognitive psychology
        </a>{' '}
        states that it’s occupied with studying mental processes, including attention, memory,
        perception, thinking, problem-solving, use of language, and creativity.
      </p>
      <p>
        The main focus of cognitive psychology is human behavior and how the processes mentioned
        above affect it.
      </p>
      <p>
        As it is aimed at studying human behavior in correlation with cognitive processes, cognitive
        psychology can help UX designers find ways to overcome psychological barriers. Thanks to
        this, they can create usable, accessible, readable, and easy-to-navigate designs.
      </p>
      <h2 id="the-effects-of-cognitive-psychology-on-ux-design">
        The Effects of Cognitive Psychology on UX Design
      </h2>
      <p>
        To illustrate the impact of cognitive psychology on UX design, let’s consider the following
        example.
      </p>
      <p>
        As a UX designer, you know that consumers value the overall experience rather than the
        details. Thus, the focus on the positive experience defines the entire UX design process.
      </p>
      <p>But what is the reason behind this principle?</p>
      <p>Cognitive psychology has an answer.</p>
      <p>
        Among cognitive psychology theories, there is one called the{' '}
        <strong>verbatim effect</strong>. This cognitive bias causes people to remember general
        information rather than separate details or the form that this information comes in.
      </p>
      <p>
        Moreover, the memory of the general experience will impact the details that people remember
        from this experience.
      </p>
      <p>
        For instance, if a person stopped at a hotel and had a poor overall experience there, they
        will only remember the unpleasant details, such as bad view, poor housekeeping, a rude
        receptionist, etc.
      </p>
      <p>
        In reality, there might have been good details about this experience, too, like a good
        breakfast or high-quality spa procedures, but if the overall experience is unpleasant, the
        memories of details will usually be unpleasant as well. This is caused mainly by the{' '}
        <a
          href="https://www.webmd.com/brain/news/20070829/bad-memories-easier-to-remember"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          evolutionary reasons
        </a>
        .
      </p>
      <h3 id="the-impact-of-the-verbatim-effect-on-ux-design">
        The impact of the verbatim effect on UX design
      </h3>
      <p>
        There are a few ways that this cognitive psychology approach can have on UX design, from
        preparing the website structure to optimizing website navigation. It also has an effect on
        the content, which is a big part of user experience.
      </p>
      <p>
        The{' '}
        <a
          href="https://pubmed.ncbi.nlm.nih.gov/18528855/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          research from the National Library of Medicine
        </a>
        , for instance, proves that the meaning of a sentence is better remembered than its form.
        The behavioral data from this research confirmed the typical verbatim effect after the
        participants performed a recognition memory test that showed the dominance of{' '}
        <strong>meaning over form</strong>.
      </p>
      <p>
        What’s more, cognitive psychology influences the decision-making processes. This means that
        you, as a UX designer, need the knowledge of basic cognitive psychology principles to make
        your designs successful.
      </p>
      <p>
        To help you understand the human behavior behind the decision-making process more, let’s
        take a look at some more cognitive psychology theories and approaches that can help you
        improve the quality of your design.
      </p>
      <h2 id="the-schema-theory">The Schema Theory</h2>
      <p>The schema theory explains why consumers prefer this design:</p>
      <p>
        <img src={img1} alt="An example of a neat, clear header." title="The Schema Theory" />
      </p>
      <p>over this one:</p>
      <p>
        <img src={img2} alt="A cluttered website." title="Design" />
      </p>
      <p>
        The reason lies in the lack of usability, the absence of proper organization, and
        categorization.
      </p>
      <p>
        The <strong>schema theory</strong> claims that all knowledge is organized into units.
        According to this approach, humans think and perceive information in schemas (units). That’s
        why we prefer it when information is broken down into categories, as it makes it easier for
        us to process it.
      </p>
      <p>
        Schemata (units of knowledge) are developed from an early age. As the person ages up, they
        add more information into these schemata units.
      </p>
      <p>
        Categorization is an essential part of the UX design process. Users expect you to break down
        the information in sizable and easy-to-understand pieces to help them process it faster.
      </p>
      <p>
        The time that it takes for a user to process information is also a part of the user
        experience.
      </p>
      <blockquote>
        <p>
          <em>
            If a consumer buys something online, there is a 90% chance that they will choose the
            store with proper categorization of information about a product because it cuts down the
            time for product research and making the final decision
          </em>
        </p>
        <p>
          <strong>
            Dustin Engelman, Researcher at{' '}
            <a href="https://studicus.com/" target="_blank" rel="noopener noreferrer nofollow">
              Studicus
            </a>
          </strong>
        </p>
      </blockquote>
      <p>
        <img
          src={img3}
          alt="An illustration of the schema theory."
          title="Example of schema theory"
        />
      </p>
      <p>
        As you can see, this cognitive psychology approach lays the foundation of one of the most
        important UX design principles – the focus on usability.
      </p>
      <h2 id="visual-perception-theories">Visual Perception Theories</h2>
      <p>
        While consumers like to think in categories, they also make many of their decisions based on
        visual perception.
      </p>
      <p>
        Because of that, it’s important to pay attention to cognitive psychology examples of
        approaches based on gestalt principles.
      </p>
      <p>So, what is a gestalt?</p>
      <p>
        We call a phenomenon{' '}
        <a
          href="https://www.britannica.com/science/Gestalt-psychology"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          a gestalt
        </a>{' '}
        when a person perceives it as a whole with no particular attention to its parts. Cognitive
        psychology claims gestalts are formed throughout our lifetime and have a significant impact
        on how we think and make decisions.
      </p>
      <p>
        Here are several basic cognitive psychology approaches or laws involving gestalt principles
        that have an impact on UX design:
      </p>
      <h3 id="the-law-of-symmetry">The Law of Symmetry</h3>
      <p>
        Symmetry creates the perception of stability and order. This happens because humans have a
        gestalt that forces us to perceive symmetrical objects as parts of the same group.
      </p>
      <p>
        This rule can help UX designers structure a website or an app, as well as{' '}
        <a href="/blog/improving-customer-experience/" target="_blank" rel="noopener noreferrer">
          improve customer experience
        </a>
        . The final product will look stable and properly organized.
      </p>
      <p>In design, there are several types of symmetry:</p>
      <ul>
        <li>
          <strong>Bilateral (reflection) symmetry</strong> - Reflection symmetry is also known as
          the mirror effect.
        </li>
        <li>
          <strong>Rotational symmetry</strong> – Also known as radial, this type of symmetry means
          that an object is rotated around the circle around one point, such as with the petals of a
          flower.
        </li>
        <li>
          <strong>Translational symmetry</strong> – This kind of symmetry applies to shifting an
          object to another position while maintaining its shape, properties, and orientation.
        </li>
      </ul>
      <p>
        For example, if we take a look at Babbel’s mobile app UI, we can notice a horizontal
        reflection symmetry. Equally sized blocks with information mirror each other, creating an
        easy-to-navigate list:
      </p>
      <p>
        <img src={img4} alt="Babbels App" title="Example of design in Babbels App" />
      </p>
      <p>
        Translation symmetry is great for content-heavy websites. “In such websites, balance and
        organization are crucial to usability, and creates a practical layout for the content,” says
        Marie Fincher, the head of content marketing at{' '}
        <a href="https://www.trustmypaper.com/" target="_blank" rel="noopener noreferrer nofollow">
          TrustMyPaper
        </a>
        .
      </p>
      <p>
        Here’s an example of how translational symmetry can help organize content and contribute to
        its readability:
      </p>
      <p>
        <img
          src={img5}
          alt="An example of transactional symmetry in web design."
          title="iCORA example"
        />
      </p>
      <p>
        These examples prove that symmetry as a visual perception gestalt plays an important role in
        the UX design process. It creates a visual organization of objects and content.
      </p>
      <h3 id="the-law-of-similarity">The Law of Similarity</h3>
      <p>
        This gestalt law states that the human eye perceives similar elements or objects as
        something complete, as one unified object or group.
      </p>
      <p>
        For instance, you can see how CNN groups the videos on its website based on this cognitive
        psychology theory:
      </p>
      <p>
        <img
          src={img6}
          alt="BBC website, an example of the law of similarity."
          title="BBC example of similarity"
        />
      </p>
      <p>
        Using the similarity law in UX design allows cleaner and more usable navigation, which, in
        turn,{' '}
        <a
          href="https://notbusinessasususal.com/8-surprising-ways-cleanliness-can-benefit-your-business/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          creates a positive first impression
        </a>{' '}
        of the product.
      </p>
      <p>The similarity gestalt is related to the law of proximity.</p>
      <h3 id="the-law-of-proximity">The Law of Proximity</h3>
      <p>
        This cognitive psychology approach indicates that the elements found in proximity to each
        other are usually perceived as a single unit.
      </p>
      <p>
        For instance, if you see the following pieces of content next to each other on a web page,
        you immediately recognize it as a feedback section:
      </p>
      <p>
        <img
          src={img7}
          alt="Reviews on a website, an illustration of the law of proximity."
          title="The Law of Proximity"
        />
      </p>
      <p>
        In design, when several elements are close to each other, they are considered as the same
        unit, even though they may have different content. This strategy is used to make navigation
        more intuitive and user-friendly.
      </p>
      <h3 id="the-chameleon-effect">The Chameleon Effect</h3>
      <p>
        As I’m talking about perception as an integral part of cognitive psychology and the UX
        design process, it’s important to mention <strong>the chameleon effect</strong>.
      </p>
      <p>
        This cognitive psychology approach states that people tend to mimic the behaviors of others.
        The chameleon effect happens unconsciously and is associated with our inner instinct to fit
        in to remain a part of a group.
      </p>
      <p>
        Among all cognitive psychology examples, this theory is the most effective in helping UX
        designers to encourage users to take the next step.
      </p>
      <p>
        For instance, Duolingo uses their mascot, a bird named Duo, to guide you through the process
        of completing each lesson:
      </p>
      <p>
        <img
          src={img8}
          alt="A screenshot from the Duolingo app, showing an encouraging comment from the app&#39;s mascot."
          title="Chameleon effect"
        />
      </p>
      <p>
        The app also uses a crying Duo if a user skips a lesson to make them feel guilty about
        forgetting to take 15 minutes a day to learn a language. A fit and sporty Duo bird appears
        whenever a user achieves a new level on the app.
      </p>
      <p>
        The chameleon effect involves a{' '}
        <a href="/blog/cognitive-bias-ux/" target="_blank" rel="noopener noreferrer">
          cognitive bias
        </a>
        . In layman’s words, this means that the context systematically affects the judgment and
        decision-making. Our example, the Duolingo app, is built on this cognitive psychology
        approach.
      </p>
      <p>
        The designers of Duolingo created certain contexts, such as crying Duo, to get the users to
        mimic a certain emotion, which consequently pushes them to a certain action.
      </p>
      <h3 id="get-in-the-head-of-your-consumers">Get In the Head of Your Consumers</h3>
      <p>There is always a psychological reason behind user behavior.</p>
      <p>
        You know that users want everything neatly organized and properly categorized. That’s the
        schema theory in action.
      </p>
      <p>
        It’s clear that consumers tend to group objects in a certain way and perceive them as a
        single unit. Gestalt laws will help you understand why they do it.
      </p>
      <p>
        To remain a part of a group, people often mimic the behaviors of other group members. You
        can use the chameleon effect to get your consumers to complete the interaction or make a
        purchase.
      </p>
      <p>
        Last but not least, now you know why consumers value the overall experience rather than
        certain parts of it. This is explained by the verbatim effect.
      </p>
      <p>
        These are some of the core cognitive psychology theories that explain the origins of the key
        UX design principles. The knowledge of these can help you become a better UX designer.
      </p>
    </Post>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: '6 Cognitive Psychology Theories to Improve UX Design',
  url: '/blog/cognitive-psychology-theories/',
  description: `See how cognitive psychology can help you become a better UX designer. Read the most important theories for UX design.`,
  author,
  img,
  imgSocial: img,
  date: '2020-06-09',
  category: '',
  group: 'Design',
  timeToRead: 8,
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    timeToRead={frontmatter.timeToRead}
  />
);
